import type { FileMetadataMap } from '@Core/lib/tasks/utils/fileTypes';
import {
  EMPTY_FILES,
  type FileMetadataStore,
  type RuntimeCollectionResult
} from '@Pages/pageRuntime/fileMetadataStore';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useSyncExternalStore
} from 'react';

export const FileMetadataContext = createContext<FileMetadataStore | null>(null);
const noSubscribe = () => () => {};

export function useFileMetadata(id: string | null | undefined) {
  const store = useContext(FileMetadataContext);
  const getSnapshot = () => (id ? store?.getSnapshot()[id] : undefined);
  return useSyncExternalStore(store?.subscribe ?? noSubscribe, getSnapshot, getSnapshot);
}

export function useRecordFileMetadata(pages: readonly RuntimeCollectionResult[]): FileMetadataMap {
  const store = useContext(FileMetadataContext);
  const [owner] = useState(() => ({}));
  const snapshots = pages.map((page) => ({
    files: page.files ?? EMPTY_FILES,
    revision: page.fileMetadataRevision ?? 0
  }));
  const files: FileMetadataMap = {};
  for (const snapshot of snapshots.toSorted((a, b) => a.revision - b.revision)) {
    Object.assign(files, snapshot.files);
  }

  // A layout effect publishes before the browser paints or loads a source.
  // An image in these records measures before it chooses one, so it reads this
  // metadata in the same synchronous pass and never requests the original first.
  useLayoutEffect(() => {
    store?.replace(owner, snapshots);
  }, [store, owner, snapshots]);
  useEffect(
    () => () => {
      store?.remove(owner);
    },
    [store, owner]
  );
  return files;
}
